/**
 * Loyalty: the app's tier rules, streak rewards and customers' points. Talks to the /loyalty group, the admin.php
 * loyalty tabs moved into Connect (2026-10-15). Non-namespaced like every other module.
 */
import { tr, trf } from "@/i18n";

const notifyError = (commit, message) =>
  commit("NOTIFICATION_MESSAGE", { status: -1, title: "Loyalty", message });

const form = (obj) => {
  const p = new URLSearchParams();
  Object.keys(obj).forEach((k) => p.append(k, obj[k]));
  return p;
};

const state = () => ({
  overview: null, // { tier_source, tiers, counts, last_run, top }
  overviewError: "",
  config: null, // { tier_source, tiers, streak_rewards_by_tier, referral_reward_pts }
  products: [], // [{ phash, name, sizes: [{ code, name }] }]
  configError: "",
  matches: [],
  searched: false,
  customer: null,
  history: [],
});

const mutations = {
  RESET_LOYALTY(s) { Object.assign(s, state()); },
  SET_LOY_OVERVIEW(s, v) { s.overview = v; s.overviewError = ""; },
  SET_LOY_OVERVIEW_ERROR(s, m) { s.overviewError = m; },
  SET_LOY_CONFIG(s, { config, products }) { s.config = config; if (products) s.products = products; s.configError = ""; },
  SET_LOY_CONFIG_ERROR(s, m) { s.configError = m; },
  SET_LOY_MATCHES(s, list) { s.matches = list || []; s.searched = true; },
  SET_LOY_CUSTOMER(s, { customer, history }) { s.customer = customer; s.history = history || []; },
};

const actions = {
  async getLoyaltyOverview({ commit }) {
    commit("LOADING", { isActiveStatus: true });
    try {
      const { data } = await window.$http.axios.get("loyalty/overview");
      if (data.status) commit("SET_LOY_OVERVIEW", data);
      else { notifyError(commit, data.message); commit("SET_LOY_OVERVIEW_ERROR", data.message || tr("The server refused the request.")); }
    } catch (e) {
      commit("SET_LOY_OVERVIEW_ERROR", tr("Could not reach the loyalty service."));
    } finally {
      commit("LOADING", { isActiveStatus: false });
    }
  },

  async getLoyaltyConfig({ commit }) {
    commit("LOADING", { isActiveStatus: true });
    try {
      const { data } = await window.$http.axios.get("loyalty/config");
      if (data.status) commit("SET_LOY_CONFIG", { config: data.config, products: data.products || [] });
      else { notifyError(commit, data.message); commit("SET_LOY_CONFIG_ERROR", data.message || tr("The server refused the request.")); }
    } catch (e) {
      commit("SET_LOY_CONFIG_ERROR", tr("Could not reach the loyalty service."));
    } finally {
      commit("LOADING", { isActiveStatus: false });
    }
  },

  async saveLoyaltyConfig({ commit }, config) {
    try {
      const { data } = await window.$http.axios.post("loyalty/config/save", form({ config: JSON.stringify(config) }));
      if (data.status) {
        commit("SET_LOY_CONFIG", { config: data.config });
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Loyalty", message: "Saved. The app uses the new rules from now on." });
        return true;
      }
      notifyError(commit, data.message); return false;
    } catch (e) { notifyError(commit, "Could not save the loyalty rules."); return false; }
  },

  async searchLoyalty({ commit }, q) {
    try {
      const { data } = await window.$http.axios.get("loyalty/lookup", { params: { q } });
      if (data.status) commit("SET_LOY_MATCHES", data.customers);
      else { commit("SET_LOY_MATCHES", []); notifyError(commit, data.message); }
    } catch (e) {
      // An old list under a new search would look like this search's answer.
      commit("SET_LOY_MATCHES", []);
      notifyError(commit, "Could not search customers.");
    }
  },

  async getLoyaltyHistory({ commit }, cID) {
    commit("SET_LOY_CUSTOMER", { customer: null, history: [] });
    try {
      const { data } = await window.$http.axios.get("loyalty/history", { params: { cID } });
      if (data.status) commit("SET_LOY_CUSTOMER", data);
      else notifyError(commit, data.message);
    } catch (e) { notifyError(commit, "Could not load the customer's points."); }
  },

  async adjustLoyaltyPoints({ commit, dispatch }, { cID, delta, reason }) {
    try {
      const { data } = await window.$http.axios.post("loyalty/adjust", form({ cID, delta, reason }));
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Loyalty", message: delta > 0 ? trf("Added {0} points.", [Math.abs(delta)]) : trf("Took back {0} points.", [Math.abs(delta)]) });
        dispatch("getLoyaltyHistory", cID);
        return true;
      }
      notifyError(commit, data.message); return false;
    } catch (e) { notifyError(commit, "Could not change the points."); return false; }
  },
};

export default { state, mutations, actions };
